import { Component, Input, OnInit, OnChanges, SimpleChanges, ElementRef, ViewChild, OnDestroy, HostListener } from '@angular/core';

export interface ChartDataPoint {
  date: Date | string;
  value: number | null;
}

export interface ChartSection {
  data: ChartDataPoint[];
  label?: string;
  color?: string;
}

export interface ChartAnnotation {
  date: Date | string;
  text?: string;
  color?: string;
  type: 'line' | 'text-only';
  textPosition?: 'top' | 'middle' | 'bottom';
  isComment?: boolean;
  offsetX?: number;
  offsetY?: number;
  band?: 'objective' | 'comment';
  laneIndex?: number;
  align?: 'left' | 'right' | 'center';
  wrappedLines?: string[];
  measuredWidth?: number;
  measuredHeight?: number;
  estimatedMarkerX?: number;
  labelLeft?: number;
  labelRight?: number;
}

export interface ChartConfig {
  lineColor?: string;
  dotColor?: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
  yAxisMin?: number;
  yAxisMax?: number;
  showValuesOnDots?: boolean;
  useValuePills?: boolean;
  dividerColor?: string;
  dividerLabel?: string;
  valuePrecision?: number;
  useDecimalTicks?: boolean;
}

@Component({
  selector: 'app-custom-line-chart',
  templateUrl: './custom-line-chart.component.html',
  styleUrls: ['./custom-line-chart.component.scss']
})
export class CustomLineChartComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly BASE_CHART_HEIGHT = 400;
  private static readonly ANNOTATION_LINE_HEIGHT = 14;
  private static readonly ANNOTATION_LANE_GAP = 8;
  private static readonly ANNOTATION_TOP_PADDING = 4;
  private static readonly ANNOTATION_BOTTOM_PADDING = 8;
  private static readonly ANNOTATION_HORIZONTAL_PADDING = 12;
  private static readonly DIVIDER_LABEL_CHAR_WIDTH = 9;

  @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('chartSvg', { static: true }) chartSvg!: ElementRef<SVGElement>;

  @Input() section1?: ChartSection;
  @Input() section2?: ChartSection;
  @Input() annotations: ChartAnnotation[] = [];
  @Input() config: ChartConfig = {};
  @Input() subtitle?: string;

  // Chart dimensions and layout
  chartWidth = 0;
  chartHeight = CustomLineChartComponent.BASE_CHART_HEIGHT;
  margin = { top: 40, right: 40, bottom: 80, left: 80 };
  plotWidth = 0;
  plotHeight = 0;

  // Section dimensions
  section1Width = 0;
  section2Width = 0;
  dividerX = 0;
  sectionGap = 0;
  section2StartX = 0;

  // Scales and ranges
  yMin = 0;
  yMax = 100;
  allDates: Date[] = [];
  xScale: Map<string, number> = new Map();
  yScale = (value: number) => value * 0;
  private annotationBandStartY: Record<'objective' | 'comment', number> = {
    objective: 0,
    comment: 0,
  };
  private annotationLaneOffsets: Record<'objective' | 'comment', number[]> = {
    objective: [],
    comment: [],
  };

  // Default configuration
  defaultConfig: ChartConfig = {
    lineColor: '#000000',
    dotColor: '#007bff',
    yAxisLabel: 'Value',
    xAxisLabel: 'Date',
    showValuesOnDots: false,
    useValuePills: false,
    dividerColor: '#cccccc',
    dividerLabel: 'Divider',
    valuePrecision: undefined,
    useDecimalTicks: false
  };

  constructor() {}

  ngOnInit(): void {
    // Delay initialization to ensure DOM is ready
    setTimeout(() => {
      this.initializeChart();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['section1'] || changes['section2'] || changes['annotations'] || changes['config']) {
      // Delay initialization to ensure DOM is ready
      setTimeout(() => {
        this.initializeChart();
      }, 0);
    }
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  /**
   * Capture the chart as SVG and convert to base64 image
   * @returns Promise<string> Base64 encoded image
   */
  async captureChartAsSvg(): Promise<string> {
    if (!this.chartSvg?.nativeElement) {
      console.warn('Chart SVG element not available for capture');
      return '';
    }

    try {
      const svgElement = this.chartSvg.nativeElement;

      // Clone the SVG to avoid modifying the original
      const clonedSvg = svgElement.cloneNode(true) as SVGElement;

      const svgRect = svgElement.getBoundingClientRect();
      const exportWidth = svgRect.width || this.chartWidth;
      const exportHeight = svgRect.height || this.chartHeight;

      // Ensure the SVG has proper dimensions
      clonedSvg.setAttribute('width', exportWidth.toString());
      clonedSvg.setAttribute('height', exportHeight.toString());

      const computedFontFamily = this.chartContainer?.nativeElement
        ? getComputedStyle(this.chartContainer.nativeElement).fontFamily
        : '';
      const fontFamily = computedFontFamily || getComputedStyle(document.body).fontFamily || 'sans-serif';

      const existingStyle = clonedSvg.getAttribute('style') || '';
      const styleSuffix = existingStyle.trim().endsWith(';') || existingStyle.trim() === '' ? '' : ';';
      clonedSvg.setAttribute('style', `${existingStyle}${styleSuffix}font-family:${fontFamily};`);

      const styleProperties = [
        'font-family',
        'font-size',
        'font-weight',
        'font-style',
        'letter-spacing',
        'text-transform',
        'text-anchor',
        'dominant-baseline',
        'fill',
        'stroke',
        'stroke-width',
        'stroke-dasharray',
        'stroke-linecap',
        'stroke-linejoin',
        'opacity',
      ];

      const originalElements = Array.from(svgElement.querySelectorAll('*'));
      const clonedElements = Array.from(clonedSvg.querySelectorAll('*'));

      originalElements.forEach((originalElement, index) => {
        const clonedElement = clonedElements[index] as SVGElement | undefined;
        if (!clonedElement) {
          return;
        }

        const computedStyle = getComputedStyle(originalElement as Element);
        styleProperties.forEach((property) => {
          const value = computedStyle.getPropertyValue(property);
          if (value) {
            clonedElement.style.setProperty(property, value);
          }
        });
      });

      // Convert SVG to string
      const svgString = new XMLSerializer().serializeToString(clonedSvg);

      // Create a data URL from the SVG
      const svgDataUrl = `data:image/svg+xml;base64,${btoa(svgString)}`;

      // Convert SVG to PNG using canvas with higher resolution
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          // Use higher resolution for better quality (2x scale factor)
          const scaleFactor = 2;
          const canvas = document.createElement('canvas');
           canvas.width = exportWidth * scaleFactor;
           canvas.height = exportHeight * scaleFactor;

          const ctx = canvas.getContext('2d');
          if (ctx) {
            // Scale the context for higher resolution
            ctx.scale(scaleFactor, scaleFactor);

            // Fill with white background
            ctx.fillStyle = 'white';
            ctx.fillRect(0, 0, exportWidth, exportHeight);

            // Draw the SVG image
            ctx.drawImage(img, 0, 0, exportWidth, exportHeight);

            // Convert to base64 with higher quality
            const base64Image = canvas.toDataURL('image/png', 1.0); // Maximum quality
            resolve(base64Image);
          } else {
            console.error('Could not get canvas context');
            resolve('');
          }
        };

        img.onerror = () => {
          console.error('Error loading SVG image');
          resolve('');
        };

        img.src = svgDataUrl;
      });

    } catch (error) {
      console.error('Error capturing chart as SVG:', error);
      return '';
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.initializeChart();
    }, 250);
  }

  private resizeTimeout: ReturnType<typeof setTimeout> | undefined;

  private initializeChart(): void {
    if (!this.chartContainer?.nativeElement || !this.chartSvg?.nativeElement) {
      return;
    }

    this.mergeConfig();
    this.calculateDimensions();
    this.processData();
    this.calculateScales();
    this.renderChart();
  }

  private mergeConfig(): void {
    this.config = { ...this.defaultConfig, ...this.config };
  }

  private calculateAnnotationBandLayout(baseTop: number): number {
    this.annotationLaneOffsets = {
      objective: [],
      comment: this.buildBandLayout('comment').offsets,
    };

    this.annotationBandStartY.objective = baseTop - 10;
    this.annotationBandStartY.comment = 15;

    return 0;
  }

  private buildBandLayout(band: 'objective' | 'comment'): { height: number; offsets: number[] } {
    const laneHeights = new Map<number, number>();

    this.annotations
      .filter((annotation) => this.getAnnotationBand(annotation) === band && this.getAnnotationLines(annotation).length > 0)
      .forEach((annotation) => {
        const laneIndex = annotation.laneIndex ?? 0;
        const currentHeight = laneHeights.get(laneIndex) ?? 0;
        laneHeights.set(laneIndex, Math.max(currentHeight, this.getAnnotationMeasuredHeight(annotation)));
      });

    const laneIndexes = Array.from(laneHeights.keys()).sort((a, b) => a - b);

    if (!laneIndexes.length) {
      return { height: 0, offsets: [] };
    }

    const offsets: number[] = [];
    let offset = 0;

    laneIndexes.forEach((laneIndex, index) => {
      offsets[laneIndex] = offset;
      offset += laneHeights.get(laneIndex) ?? CustomLineChartComponent.ANNOTATION_LINE_HEIGHT;

      if (index < laneIndexes.length - 1) {
        offset += CustomLineChartComponent.ANNOTATION_LANE_GAP;
      }
    });

    return { height: offset, offsets };
  }

  private calculateDimensions(): void {
    // Update chart dimensions based on container
    const containerRect = this.chartContainer.nativeElement.getBoundingClientRect();

    // Make chart responsive - use container width, accounting for padding
    this.chartWidth = containerRect.width - 40; // Subtract padding (e.g., 20px on each side)

    // Adjust margins for smaller screens
    let baseMargin: { top: number; right: number; bottom: number; left: number };
    if (this.chartWidth < 600) {
      baseMargin = { top: 30, right: 20, bottom: 100, left: 60 }; // More bottom space for rotated labels
    } else if (this.chartWidth < 800) {
      baseMargin = { top: 35, right: 30, bottom: 90, left: 70 };
    } else {
      baseMargin = { top: 40, right: 40, bottom: 80, left: 80 };
    }

    const annotationExtraTop = this.calculateAnnotationBandLayout(baseMargin.top);
    this.chartHeight = CustomLineChartComponent.BASE_CHART_HEIGHT + annotationExtraTop;
    this.margin = {
      ...baseMargin,
      top: baseMargin.top + annotationExtraTop,
    };

    this.plotWidth = this.chartWidth - this.margin.left - this.margin.right;
    this.plotHeight = this.chartHeight - this.margin.top - this.margin.bottom;

    // Calculate section widths based on data points
    const section1Points = this.section1?.data?.length || 0;
    const section2Points = this.section2?.data?.length || 0;
    const totalPoints = section1Points + section2Points;

    if (totalPoints === 0) {
      this.section1Width = 0;
      this.section2Width = 0;
      this.sectionGap = 0;
      this.section2StartX = 0;
      this.dividerX = 0;
      return;
    }

    this.sectionGap = 0;

    if (section1Points === 0) {
      this.section1Width = 0;
      this.section2Width = this.plotWidth;
      this.section2StartX = 0;
      this.dividerX = 0;
      return;
    }

    if (section2Points === 0) {
      this.section1Width = this.plotWidth;
      this.section2Width = 0;
      this.section2StartX = this.section1Width;
      this.dividerX = 0;
      return;
    }

    // Both sections have data - reserve space for a visible divider
    const baseGap = Math.min(Math.max(this.plotWidth * 0.015, 12), 40);
    this.sectionGap = Math.min(baseGap, this.plotWidth);

    if (this.plotWidth - this.sectionGap <= 0) {
      this.sectionGap = Math.max(this.plotWidth - 1, 0);
    }

    const availableWidth = Math.max(this.plotWidth - this.sectionGap, 0);
    const minSectionWidth = availableWidth === 0 ? 0 : Math.min(80, availableWidth / 2);

    // For baseline (section1), use a smaller fixed width since it usually has just one point
    // Reduce the baseline section size and give more space to treatment data
    const section1Ratio = Math.max(section1Points / totalPoints, 0.12); // Reduced from 0.2 to 0.12
    this.section1Width = Math.max(availableWidth * section1Ratio, minSectionWidth);
    this.section1Width = Math.min(this.section1Width, availableWidth - minSectionWidth);

    this.section2Width = availableWidth - this.section1Width;

    if (this.section2Width < minSectionWidth) {
      this.section2Width = minSectionWidth;
      this.section1Width = availableWidth - this.section2Width;
    }

    this.section2StartX = this.section1Width + this.sectionGap;
    this.dividerX = this.section1Width + this.sectionGap / 2;
  }

  private processData(): void {
    this.allDates = [];

    // Collect all dates from both sections
    if (this.section1?.data) {
      this.section1.data.forEach(point => {
        const date = this.createSafeDate(point.date);
        if (!isNaN(date.getTime())) {
          this.allDates.push(date);
        }
      });
    }

    if (this.section2?.data) {
      this.section2.data.forEach(point => {
        const date = this.createSafeDate(point.date);
        if (!isNaN(date.getTime())) {
          this.allDates.push(date);
        }
      });
    }

    // Sort dates
    this.allDates.sort((a, b) => a.getTime() - b.getTime());

    // Remove duplicates
    this.allDates = this.allDates.filter((date, index, arr) =>
      index === 0 || date.getTime() !== arr[index - 1].getTime()
    );
  }

  private calculateScales(): void {
    // Calculate Y scale
    const allValues: number[] = [];

    if (this.section1?.data) {
      allValues.push(...this.section1.data.filter(d => d.value !== null).map(d => d.value as number));
    }

    if (this.section2?.data) {
      allValues.push(...this.section2.data.filter(d => d.value !== null).map(d => d.value as number));
    }

    if (this.config.yAxisMin !== undefined && this.config.yAxisMax !== undefined) {
      this.yMin = this.config.yAxisMin;
      this.yMax = this.config.yAxisMax;
    } else if (allValues.length > 0) {
      const dataMax = Math.max(...allValues);

      if (this.config.useDecimalTicks) {
        this.yMin = 0;
        this.yMax = this.calculateNiceDecimalMax(dataMax, this.config.valuePrecision ?? 1);
      }
      // For maladaptive behaviors (Frequency), always start from 0
      else if (this.config.yAxisLabel === 'Frequency') {
        this.yMin = 0;
        // Set max to a nice round number above the data max
        this.yMax = this.calculateNiceMax(dataMax);
      } else if (this.config.yAxisLabel === '% of Trials') {
        // For replacement behaviors (percentages), also start from 0 and use clean max
        this.yMin = 0;
        this.yMax = this.calculateNicePercentageMax(dataMax);
      } else {
        // For other cases, use the original logic
        this.yMin = Math.min(...allValues);
        this.yMax = Math.max(...allValues);

        // Add some padding
        const padding = (this.yMax - this.yMin) * 0.1;
        this.yMin = Math.max(0, this.yMin - padding);
        this.yMax = this.yMax + padding;
      }
    }

    this.yScale = (value: number) => {
      return this.plotHeight - ((value - this.yMin) / (this.yMax - this.yMin)) * this.plotHeight;
    };

    // Calculate X scale for each section
    this.calculateXScales();
  }

  private calculateXScales(): void {
    this.xScale.clear();

    // Section 1 dates - add padding so baseline doesn't sit on Y-axis
    // Process ALL dates including those with null values
    if (this.section1?.data && this.section1Width > 0) {
      const padding = this.section1Width * 0.3; // 30% padding from left edge
      const usableWidth = Math.max(this.section1Width - padding, 0);
      const safeDates = this.section1.data.map(point => this.createSafeDate(point.date));
      const validTimes = safeDates
        .map(date => date.getTime())
        .filter(time => !isNaN(time));

      if (validTimes.length > 0) {
        const minTime = Math.min(...validTimes);
        const maxTime = Math.max(...validTimes);
        const timeRange = maxTime - minTime;

        // Map ALL dates (including those with null values) to X positions
        safeDates.forEach((safeDate) => {
          const time = safeDate.getTime();
          if (isNaN(time)) {
            return;
          }

          const ratio = timeRange === 0 ? 0 : (time - minTime) / timeRange;
          const x = padding + ratio * usableWidth;
          const dateKey = this.getDateKey(safeDate);
          this.xScale.set(dateKey, x);
        });
      }
    }

    // Section 2 dates - process ALL dates including those with null values
    if (this.section2?.data && this.section2Width > 0) {
      const safeDates = this.section2.data.map(point => this.createSafeDate(point.date));
      const validTimes = safeDates
        .map(date => date.getTime())
        .filter(time => !isNaN(time));

      if (validTimes.length > 0) {
        const minTime = Math.min(...validTimes);
        const maxTime = Math.max(...validTimes);
        const timeRange = maxTime - minTime;

        // Map ALL dates (including those with null values) to X positions
        safeDates.forEach((safeDate) => {
          const time = safeDate.getTime();
          if (isNaN(time)) {
            return;
          }

          const ratio = timeRange === 0 ? 0 : (time - minTime) / timeRange;
          const x = this.section2StartX + ratio * this.section2Width;
          const dateKey = this.getDateKey(safeDate);

          this.xScale.set(dateKey, x);
        });
      }
    }

    this.updateDividerPositionFromData();
  }

  private renderChart(): void {
    const svg = this.chartSvg.nativeElement;

    // Clear existing content except the template elements
    this.clearDynamicContent(svg);

    // Render grid lines
    this.renderGridLines(svg);

    // Render axis ticks and labels
    this.renderAxisTicks(svg);

    // Render data sections
    this.renderSection1(svg);
    this.renderSection2(svg);

    // Render annotations
    this.renderAnnotations(svg);
  }

  private clearDynamicContent(svg: SVGElement): void {
    // Clear dynamically generated content
    const gridLinesY = svg.querySelector('.grid-lines-y');
    const gridLinesX = svg.querySelector('.grid-lines-x');
    const yAxisTicks = svg.querySelector('.y-axis-ticks');
    const xAxisTicks = svg.querySelector('.x-axis-ticks');
    const section1 = svg.querySelector('.section-1');
    const section2 = svg.querySelector('.section-2');
    const annotations = svg.querySelector('.annotations');

    [gridLinesY, gridLinesX, yAxisTicks, xAxisTicks, section1, section2, annotations].forEach(element => {
      if (element) {
        element.innerHTML = '';
      }
    });
  }

  private renderGridLines(svg: SVGElement): void {
    const gridLinesY = svg.querySelector('.grid-lines-y');
    const gridLinesX = svg.querySelector('.grid-lines-x');

    if (!gridLinesY || !gridLinesX) return;

    // Y-axis grid lines
    const yTicks = this.generateYTicks();
    yTicks.forEach(tick => {
      const y = this.margin.top + this.getYPosition(tick);
      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', this.margin.left.toString());
      line.setAttribute('y1', y.toString());
      line.setAttribute('x2', (this.margin.left + this.plotWidth).toString());
      line.setAttribute('y2', y.toString());
      gridLinesY.appendChild(line);
    });

    // X-axis grid lines for major date points
    this.allDates.forEach((date, index) => {
      if (index % Math.ceil(this.allDates.length / 10) === 0) { // Show every nth line
        const x = this.margin.left + this.getXPosition(date);
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', x.toString());
        line.setAttribute('y1', this.margin.top.toString());
        line.setAttribute('x2', x.toString());
        line.setAttribute('y2', (this.margin.top + this.plotHeight).toString());
        gridLinesX.appendChild(line);
      }
    });
  }

  private renderAxisTicks(svg: SVGElement): void {
    const yAxisTicks = svg.querySelector('.y-axis-ticks');
    const xAxisTicks = svg.querySelector('.x-axis-ticks');

    if (!yAxisTicks || !xAxisTicks) return;

    // Y-axis ticks and labels
    const yTicks = this.generateYTicks();
    yTicks.forEach(tick => {
      const y = this.margin.top + this.getYPosition(tick);

      // Tick line
      const tickLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      tickLine.setAttribute('x1', (this.margin.left - 5).toString());
      tickLine.setAttribute('y1', y.toString());
      tickLine.setAttribute('x2', this.margin.left.toString());
      tickLine.setAttribute('y2', y.toString());
      yAxisTicks.appendChild(tickLine);

      // Tick label
      const tickLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      tickLabel.setAttribute('x', (this.margin.left - 10).toString());
      tickLabel.setAttribute('y', y.toString());
      tickLabel.setAttribute('text-anchor', 'end');
      tickLabel.setAttribute('dominant-baseline', 'middle');
      tickLabel.textContent = this.formatYValue(tick);
      yAxisTicks.appendChild(tickLabel);
    });

    // X-axis ticks and labels
    this.renderXAxisTicks(xAxisTicks);
  }

  private renderXAxisTicks(xAxisTicks: Element): void {
    // Section 1 ticks - render ALL dates (including those with null values)
    if (this.section1?.data && this.section1Width > 0) {
      this.section1.data.forEach((point) => {
        const date = this.createSafeDate(point.date);
        const x = this.margin.left + this.getXPosition(date);

        // Tick line
        const tickLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        tickLine.setAttribute('x1', x.toString());
        tickLine.setAttribute('y1', (this.margin.top + this.plotHeight).toString());
        tickLine.setAttribute('x2', x.toString());
        tickLine.setAttribute('y2', (this.margin.top + this.plotHeight + 5).toString());
        tickLine.setAttribute('stroke', '#333333');
        tickLine.setAttribute('stroke-width', '1');
        xAxisTicks.appendChild(tickLine);

        // Tick label (vertical)
        const tickLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        tickLabel.setAttribute('x', x.toString());
        tickLabel.setAttribute('y', (this.margin.top + this.plotHeight + 15).toString());
        tickLabel.setAttribute('text-anchor', 'start');
        tickLabel.setAttribute('transform', `rotate(90, ${x}, ${this.margin.top + this.plotHeight + 15})`);
        tickLabel.setAttribute('fill', '#666666');
        tickLabel.setAttribute('font-size', '11px');
        tickLabel.textContent = this.formatDate(date);
        xAxisTicks.appendChild(tickLabel);
      });
    }

    // Section 2 ticks - render ALL dates (including those with null values)
    if (this.section2?.data && this.section2Width > 0) {
      this.section2.data.forEach((point) => {
        const date = this.createSafeDate(point.date);
        const x = this.margin.left + this.getXPosition(date);

        // Tick line
        const tickLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        tickLine.setAttribute('x1', x.toString());
        tickLine.setAttribute('y1', (this.margin.top + this.plotHeight).toString());
        tickLine.setAttribute('x2', x.toString());
        tickLine.setAttribute('y2', (this.margin.top + this.plotHeight + 5).toString());
        tickLine.setAttribute('stroke', '#333333');
        tickLine.setAttribute('stroke-width', '1');
        xAxisTicks.appendChild(tickLine);

        // Tick label (vertical)
        const tickLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        tickLabel.setAttribute('x', x.toString());
        tickLabel.setAttribute('y', (this.margin.top + this.plotHeight + 15).toString());
        tickLabel.setAttribute('text-anchor', 'start');
        tickLabel.setAttribute('transform', `rotate(90, ${x}, ${this.margin.top + this.plotHeight + 15})`);
        tickLabel.setAttribute('fill', '#666666');
        tickLabel.setAttribute('font-size', '11px');
        tickLabel.textContent = this.formatDate(date);
        xAxisTicks.appendChild(tickLabel);
      });
    }
  }

  private generateYTicks(): number[] {
    if (this.config.useDecimalTicks) {
      return this.generateDecimalTicks();
    }

    // For both maladaptive behaviors and replacement behaviors, use clean whole number intervals
    if (this.config.yAxisLabel === 'Frequency') {
      return this.generateMaladaptiveTicks();
    } else if (this.config.yAxisLabel === '% of Trials') {
      return this.generatePercentageTicks();
    }

    // Fallback to original logic for other cases
    const tickCount = 6;
    const ticks: number[] = [];
    const step = (this.yMax - this.yMin) / (tickCount - 1);

    for (let i = 0; i < tickCount; i++) {
      ticks.push(this.yMin + (step * i));
    }

    return ticks;
  }

  private generateMaladaptiveTicks(): number[] {
    const maxValue = Math.ceil(this.yMax);

    // Determine the best interval based on the max value
    let interval: number;

    if (maxValue <= 10) {
      interval = 2;
    } else if (maxValue <= 25) {
      interval = 5;
    } else if (maxValue <= 50) {
      interval = 10;
    } else if (maxValue <= 100) {
      interval = 20;
    } else if (maxValue <= 200) {
      interval = 50;
    } else if (maxValue <= 500) {
      interval = 100;
    } else if (maxValue <= 1000) {
      interval = 200;
    } else if (maxValue <= 2000) {
      interval = 500;
    } else if (maxValue <= 5000) {
      interval = 1000;
    } else {
      interval = 100;
    }

    // Generate ticks starting from 0
    const ticks: number[] = [];
    let currentTick = 0;

    while (currentTick <= maxValue) {
      ticks.push(currentTick);
      currentTick += interval;
    }

    // Ensure we have at least one tick above the max value for better visualization
    if (ticks[ticks.length - 1] < maxValue) {
      ticks.push(currentTick);
    }

    return ticks;
  }

  private generatePercentageTicks(): number[] {
    // For percentages, use clean intervals of 10, 20, or 25
    const maxValue = Math.ceil(this.yMax);

    let interval: number;

    if (maxValue <= 50) {
      interval = 10; // 0, 10, 20, 30, 40, 50
    } else if (maxValue <= 100) {
      interval = 20; // 0, 20, 40, 60, 80, 100
    } else {
      interval = 25; // 0, 25, 50, 75, 100, 125 (though percentages shouldn't exceed 100)
    }

    // Generate ticks starting from 0
    const ticks: number[] = [];
    let currentTick = 0;

    while (currentTick <= maxValue) {
      ticks.push(currentTick);
      currentTick += interval;
    }

    // Ensure we have at least one tick above the max value for better visualization
    if (ticks[ticks.length - 1] < maxValue) {
      ticks.push(currentTick);
    }

    return ticks;
  }

  private calculateNiceMax(dataMax: number): number {
    // Calculate a nice maximum value that's above the data max
    let niceMax: number;

    if (dataMax <= 10) {
      niceMax = Math.ceil(dataMax / 2) * 2; // Round up to nearest 2
    } else if (dataMax <= 25) {
      niceMax = Math.ceil(dataMax / 5) * 5; // Round up to nearest 5
    } else if (dataMax <= 50) {
      niceMax = Math.ceil(dataMax / 10) * 10; // Round up to nearest 10
    } else if (dataMax <= 100) {
      niceMax = Math.ceil(dataMax / 20) * 20; // Round up to nearest 20
    } else if (dataMax <= 200) {
      niceMax = Math.ceil(dataMax / 50) * 50; // Round up to nearest 50
    } else {
      niceMax = Math.ceil(dataMax / 100) * 100; // Round up to nearest 100
    }

    // Ensure the nice max is at least slightly above the data max
    return Math.max(niceMax, dataMax + 1);
  }

  private calculateNicePercentageMax(dataMax: number): number {
    // For percentages, use clean maximum values
    if (dataMax <= 50) {
      return Math.ceil(dataMax / 10) * 10; // Round up to nearest 10 (10, 20, 30, 40, 50)
    } else if (dataMax <= 100) {
      return Math.ceil(dataMax / 20) * 20; // Round up to nearest 20 (60, 80, 100)
    } else {
      // Percentages shouldn't exceed 100, but just in case
      return Math.ceil(dataMax / 25) * 25; // Round up to nearest 25
    }
  }

  private calculateNiceDecimalMax(dataMax: number, precision: number): number {
    if (!Number.isFinite(dataMax) || dataMax <= 0) {
      return 1 / Math.pow(10, precision);
    }

    const scale = Math.pow(10, precision);
    const scaledMax = Math.ceil(dataMax * scale);
    const niceScaledMax = this.calculateNiceMax(scaledMax);
    return niceScaledMax / scale;
  }

  private generateDecimalTicks(): number[] {
    const precision = this.config.valuePrecision ?? 1;
    const scale = Math.pow(10, precision);
    const scaledMax = Math.ceil(this.yMax * scale);

    if (scaledMax <= 0) {
      return [0];
    }

    let interval: number;

    if (scaledMax <= 10) {
      interval = 1;
    } else if (scaledMax <= 25) {
      interval = 2;
    } else if (scaledMax <= 50) {
      interval = 5;
    } else if (scaledMax <= 100) {
      interval = 10;
    } else if (scaledMax <= 200) {
      interval = 50;
    } else {
      interval = 100;
    }

    const ticks: number[] = [];
    let currentTick = 0;

    while (currentTick <= scaledMax) {
      ticks.push(parseFloat((currentTick / scale).toFixed(precision)));
      currentTick += interval;
    }

    const maxTick = parseFloat((scaledMax / scale).toFixed(precision));
    if (ticks[ticks.length - 1] < maxTick) {
      ticks.push(parseFloat((currentTick / scale).toFixed(precision)));
    }

    return ticks;
  }

  private formatYValue(value: number): string {
    // For both maladaptive behaviors (Frequency) and replacement behaviors (% of Trials), show whole numbers
    if (this.config.valuePrecision !== undefined) {
      const precision = this.config.valuePrecision;
      if (!Number.isFinite(value)) {
        return '';
      }
      const fixed = value.toFixed(precision);
      if (precision === 0) {
        return fixed;
      }
      return fixed.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
    }

    if (value >= 1000) {
      return (value / 1000).toFixed(0) + 'k';
    }
    return Math.round(value).toString();
  }

  private renderSection1(svg: SVGElement): void {
    const section1Element = svg.querySelector('.section-1');
    if (!section1Element || !this.section1?.data || this.section1.data.length === 0) {
      return;
    }

    this.renderSectionData(section1Element, this.section1);
  }

  private renderSection2(svg: SVGElement): void {
    const section2Element = svg.querySelector('.section-2');
    if (!section2Element || !this.section2?.data || this.section2.data.length === 0) {
      return;
    }

    this.renderSectionData(section2Element, this.section2);
  }

  private renderSectionData(container: Element, section: ChartSection): void {
    const validPoints = section.data.filter(point => point.value !== null);
    if (validPoints.length === 0) {
      return;
    }

    // Create path for the line
    const pathData = this.createLinePath(section.data);
    if (pathData) {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', pathData);
      path.setAttribute('class', 'data-line');
      path.setAttribute('stroke', section.color || this.config.lineColor || '#000000');
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke-width', '2');
      container.appendChild(path);
    }

    // Render points
    section.data.forEach((point) => {
      if (point.value !== null) {
        this.renderDataPoint(container, point);
      }
    });
  }

  private createLinePath(data: ChartDataPoint[]): string | null {
    const pathSegments: string[] = [];
    let hasStarted = false;

    data.forEach((point) => {
      const date = this.createSafeDate(point.date);
      const x = this.margin.left + this.getXPosition(date);
      const y = this.margin.top + (point.value !== null ? this.getYPosition(point.value) : 0);

      if (point.value !== null) {
        if (!hasStarted) {
          pathSegments.push(`M ${x} ${y}`);
          hasStarted = true;
        } else {
          pathSegments.push(`L ${x} ${y}`);
        }
      } else {
        // Break the line for null values
        hasStarted = false;
      }
    });

    return pathSegments.length > 0 ? pathSegments.join(' ') : null;
  }

  private renderDataPoint(container: Element, point: ChartDataPoint): void {
    if (point.value === null) return;

    const date = this.createSafeDate(point.date);
    const x = this.margin.left + this.getXPosition(date);
    const y = this.margin.top + this.getYPosition(point.value);

    if (this.config.useValuePills) {
      this.renderValuePill(container, x, y, point.value);
    } else {
      this.renderDot(container, x, y, point.value);
    }
  }

  private renderDot(container: Element, x: number, y: number, value: number): void {
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', x.toString());
    circle.setAttribute('cy', y.toString());
    circle.setAttribute('r', '4');
    circle.setAttribute('class', 'data-point dot');
    circle.setAttribute('fill', this.config.dotColor || '#007bff');
    container.appendChild(circle);

    if (this.config.showValuesOnDots) {
      this.renderValueLabel(container, x, y - 15, value, 'above-dot');
    }
  }

  private renderValuePill(container: Element, x: number, y: number, value: number): void {
    const displayValue = this.formatYValue(value);
    const valueText = displayValue;
    const textWidth = valueText.length * 6 + 8; // Approximate width
    const pillWidth = Math.max(textWidth, 20);
    const pillHeight = 16;

    // Pill background
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', (x - pillWidth / 2).toString());
    rect.setAttribute('y', (y - pillHeight / 2).toString());
    rect.setAttribute('width', pillWidth.toString());
    rect.setAttribute('height', pillHeight.toString());
    rect.setAttribute('rx', '8');
    rect.setAttribute('ry', '8');
    rect.setAttribute('class', 'data-point pill');
    rect.setAttribute('fill', this.config.dotColor || '#007bff');
    container.appendChild(rect);

    // Value text inside pill
    this.renderValueLabel(container, x, y, value, 'in-pill', displayValue);
  }

  private renderValueLabel(container: Element, x: number, y: number, value: number, type: string, displayOverride?: string): void {
    const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('x', x.toString());
    text.setAttribute('y', y.toString());
    text.setAttribute('class', `value-label ${type}`);
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('dominant-baseline', 'middle');
    text.textContent = displayOverride ?? this.formatYValue(value);
    container.appendChild(text);
  }

  private renderAnnotations(svg: SVGElement): void {
    const annotationsElement = svg.querySelector('.annotations');
    if (!annotationsElement || !this.annotations || this.annotations.length === 0) return;

    this.annotations.forEach((annotation) => {
      const date = this.createSafeDate(annotation.date);
      const dateKey = this.getDateKey(date);

      let x: number;

      if (this.xScale.has(dateKey)) {
        x = this.margin.left + this.getXPosition(date);
      } else {
        const section2Position = this.getInterpolatedPosition(date);
        if (section2Position === null) {
          return;
        }
        x = this.margin.left + section2Position;
      }

      const minSection2X = this.margin.left + this.section2StartX;
      const maxSection2X = this.margin.left + this.plotWidth;

      if (x < minSection2X || x > maxSection2X) {
        return;
      }

      const isComment = annotation.isComment === true;
      const lineColor = annotation.color || (isComment ? '#000000' : '#ff6b6b');

      if (annotation.type === 'line' || !annotation.type) {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', x.toString());
        line.setAttribute('y1', this.margin.top.toString());
        line.setAttribute('x2', x.toString());
        line.setAttribute('y2', (this.margin.top + this.plotHeight).toString());
        const lineClasses = ['annotation-line'];
        if (isComment) {
          lineClasses.push('comment');
        }
        line.setAttribute('class', lineClasses.join(' '));
        line.setAttribute('stroke', lineColor);
        line.setAttribute('stroke-width', isComment ? '1.5' : '2');
        annotationsElement.appendChild(line);
      }

      const annotationLines = this.getAnnotationLines(annotation);
      if (annotationLines.length > 0) {
        const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        const textClasses = ['annotation-text', annotation.textPosition || 'top'];
        if (isComment) {
          textClasses.push('comment');
        }

        const measuredWidth = this.getAnnotationMeasuredWidth(annotation);
        const textX = this.getAnnotationTextX(x, annotation, measuredWidth);
        const textY = this.getAnnotationTextY(annotation);

        text.setAttribute('x', textX.toString());
        text.setAttribute('y', textY.toString());
        text.setAttribute('class', textClasses.join(' '));
        text.setAttribute('fill', lineColor);
        text.setAttribute('text-anchor', this.getAnnotationTextAnchor(annotation));
        text.setAttribute('dominant-baseline', 'hanging');

        annotationLines.forEach((lineText, index) => {
          const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
          tspan.setAttribute('x', textX.toString());
          if (index > 0) {
            tspan.setAttribute('dy', CustomLineChartComponent.ANNOTATION_LINE_HEIGHT.toString());
          }
          tspan.textContent = lineText;
          text.appendChild(tspan);
        });

        annotationsElement.appendChild(text);
      }
    });
  }

  private getAnnotationTextY(annotation: ChartAnnotation): number {
    const band = this.getAnnotationBand(annotation);
    const laneIndex = annotation.laneIndex ?? 0;
    const laneOffset = this.annotationLaneOffsets[band][laneIndex] ?? 0;

    if (band === 'comment') {
      return this.margin.top + this.annotationBandStartY.comment + laneOffset + (annotation.offsetY ?? 0);
    }

    if (band === 'objective') {
      return this.annotationBandStartY.objective + (annotation.offsetY ?? 0);
    }

    if ((annotation.textPosition || 'top') === 'middle') {
      return this.margin.top + this.plotHeight / 2;
    }

    if ((annotation.textPosition || 'top') === 'bottom') {
      return this.margin.top + this.plotHeight + 20;
    }

    return this.annotationBandStartY[band] + laneOffset + (annotation.offsetY ?? 0);
  }

  private getAnnotationTextX(x: number, annotation: ChartAnnotation, measuredWidth: number): number {
    const minTextX = this.margin.left + 4;
    const maxTextX = this.margin.left + this.plotWidth - 4;
    const align = annotation.align ?? (annotation.isComment ? 'right' : 'center');

    if (align === 'left') {
      const clampedLeft = Math.max(
        minTextX,
        Math.min(x - (annotation.offsetX ?? 6) - measuredWidth, maxTextX - measuredWidth)
      );

      return clampedLeft + measuredWidth;
    }

    if (align === 'right') {
      const clampedLeft = Math.max(
        minTextX,
        Math.min(x + (annotation.offsetX ?? 6), maxTextX - measuredWidth)
      );

      return clampedLeft;
    }

    const halfWidth = measuredWidth / 2;
    const shiftedX = x + (annotation.offsetX ?? 0);
    let centeredX = Math.max(minTextX + halfWidth, Math.min(shiftedX, maxTextX - halfWidth));

    if (this.getAnnotationBand(annotation) === 'objective') {
      const minObjectiveCenter = this.getObjectiveMinCenter(measuredWidth);
      centeredX = Math.max(centeredX, minObjectiveCenter);
      centeredX = Math.min(centeredX, maxTextX - halfWidth);
    }

    return centeredX;
  }

  private getObjectiveMinCenter(measuredWidth: number): number {
    if (!this.config.dividerLabel || !this.section1?.data?.length || !this.section2?.data?.length || this.dividerX <= 0) {
      return this.margin.left + 4 + measuredWidth / 2;
    }

    const dividerCenter = this.margin.left + this.dividerX;
    const dividerLabelWidth = this.config.dividerLabel.length * CustomLineChartComponent.DIVIDER_LABEL_CHAR_WIDTH;
    const dividerRightEdge =
      dividerCenter + dividerLabelWidth / 2 + CustomLineChartComponent.ANNOTATION_HORIZONTAL_PADDING;

    return dividerRightEdge + measuredWidth / 2;
  }

  private getAnnotationTextAnchor(annotation: ChartAnnotation): 'start' | 'middle' | 'end' {
    const align = annotation.align ?? (annotation.isComment ? 'right' : 'center');

    if (align === 'left') {
      return 'end';
    }

    if (align === 'right') {
      return 'start';
    }

    return 'middle';
  }

  private getAnnotationBand(annotation: ChartAnnotation): 'objective' | 'comment' {
    if (annotation.band) {
      return annotation.band;
    }

    return annotation.isComment ? 'comment' : 'objective';
  }

  private getAnnotationLines(annotation: ChartAnnotation): string[] {
    if (annotation.wrappedLines && annotation.wrappedLines.length > 0) {
      return annotation.wrappedLines;
    }

    if (annotation.text) {
      return [annotation.text];
    }

    return [];
  }

  private getAnnotationMeasuredWidth(annotation: ChartAnnotation): number {
    if (typeof annotation.measuredWidth === 'number' && annotation.measuredWidth > 0) {
      return annotation.measuredWidth;
    }

    return Math.max(...this.getAnnotationLines(annotation).map((line) => line.length * 6), 0);
  }

  private getAnnotationMeasuredHeight(annotation: ChartAnnotation): number {
    if (typeof annotation.measuredHeight === 'number' && annotation.measuredHeight > 0) {
      return annotation.measuredHeight;
    }

    const lineCount = Math.max(this.getAnnotationLines(annotation).length, 1);
    return lineCount * CustomLineChartComponent.ANNOTATION_LINE_HEIGHT;
  }

  // Utility methods
  formatDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC', // Force UTC to avoid timezone shifts
    });
  }

  // Helper method to create timezone-safe dates
  private createSafeDate(dateInput: Date | string): Date {
    let date: Date;

    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateInput)) {
      const [year, month, day] = dateInput.split('T')[0].split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    } else {
      date = new Date(dateInput);
    }

    if (isNaN(date.getTime())) {
      return date;
    }

    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  }

  private getDateKey(date: Date): string {
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getXPosition(date: Date | string): number {
    const safeDate = this.createSafeDate(date);
    if (isNaN(safeDate.getTime())) {
      return 0;
    }

    const dateKey = this.getDateKey(safeDate);
    return this.xScale.get(dateKey) ?? 0;
  }

  getYPosition(value: number): number {
    return this.yScale(value);
  }

  // Helper method to interpolate position for dates that fall between data points in section 2
  private getInterpolatedPosition(date: Date): number | null {
    if (!this.section2?.data || this.section2.data.length === 0) {
      return null;
    }

    const normalizedTarget = this.createSafeDate(date);
    const targetTime = normalizedTarget.getTime();

    const section2Dates = this.section2.data
      .map(d => this.createSafeDate(d.date))
      .filter(d => !isNaN(d.getTime()));

    if (section2Dates.length === 0 || isNaN(targetTime)) {
      return null;
    }

    const section2Times = section2Dates.map(d => d.getTime());
    const minTime = Math.min(...section2Times);
    const maxTime = Math.max(...section2Times);

    if (targetTime < minTime || targetTime > maxTime) {
      return null;
    }

    if (maxTime === minTime) {
      return this.section2StartX;
    }

    // Interpolate position within section 2
    const ratio = (targetTime - minTime) / (maxTime - minTime);
    return this.section2StartX + (ratio * this.section2Width);
  }

  private updateDividerPositionFromData(): void {
    if (!this.section1?.data?.length || !this.section2?.data?.length) {
      return;
    }

    const baselineXs = this.section1.data
      .filter(point => point.value !== null)
      .map(point => this.createSafeDate(point.date))
      .map(date => this.xScale.get(this.getDateKey(date)))
      .filter((value): value is number => typeof value === 'number');

    const treatmentXs = this.section2.data
      .filter(point => point.value !== null)
      .map(point => this.createSafeDate(point.date))
      .map(date => this.xScale.get(this.getDateKey(date)))
      .filter((value): value is number => typeof value === 'number');

    if (!baselineXs.length || !treatmentXs.length) {
      return;
    }

    const baselineRightEdge = Math.max(...baselineXs);
    const treatmentLeftEdge = Math.min(...treatmentXs);

    if (!isFinite(baselineRightEdge) || !isFinite(treatmentLeftEdge)) {
      return;
    }

    if (baselineRightEdge >= treatmentLeftEdge) {
      this.dividerX = this.section1Width + this.sectionGap / 2;
      return;
    }

    this.dividerX = baselineRightEdge + (treatmentLeftEdge - baselineRightEdge) / 2;
  }
}
